import { useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Home, Wrench, FileText, UserCircle, Mail, Phone, FileDown, CalendarDays, HandCoins } from "lucide-react";
import { OFFER_STATUS, fmtMoney } from "@/pages/biz/Offers";
import { OfferNotes } from "@/components/OfferNotes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Shell } from "@/components/Shell";
import { PageHeader, Loading, StatusBadge, EmptyState, StatCard } from "@/components/common";
import { AccountCard } from "@/components/AccountCard";
import { api, useFetch, errMsg, money, fmtDate, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Overview() {
  const { data } = useFetch("/landlord/overview");
  if (!data) return <Loading />;
  const cur = data.agent.currency;
  return (
    <div data-testid="landlord-overview-page">
      <PageHeader eyebrow="Landlord portal" title={`Hello ${data.landlord.name.split(" ")[0]}`} subtitle={`Your portfolio, managed by ${data.agent.name}.`} />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard label="Rent due this month" value={money(data.rent_due_month, cur)} testid="ll-rent-due" />
        <StatCard label="Collected this month" value={money(data.rent_collected_month, cur)} tone="green" testid="ll-collected" />
        <StatCard label="Arrears" value={money(data.arrears, cur)} tone={data.arrears ? "red" : "slate"} testid="ll-arrears" />
      </div>
      <div className="panel mb-6 text-sm flex flex-wrap gap-6" data-testid="ll-agent-contact">
        <span className="font-semibold">{data.agent.name}</span>
        {data.agent.email && <a href={`mailto:${data.agent.email}`} className="flex items-center gap-1"><Mail className="w-4 h-4" />{data.agent.email}</a>}
        {data.agent.phone && <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{data.agent.phone}</span>}
      </div>
      {data.properties.length === 0 ? <EmptyState icon={Home} title="No properties assigned yet" /> : (
        <div className="grid md:grid-cols-2 gap-6">
          {data.properties.map((p) => (
            <div key={p.id} className="panel" data-testid={`ll-property-${p.id}`}>
              <h3 className="text-lg font-bold">{p.name}</h3>
              <p className="text-sm text-slate-500">{p.address}</p>
              <div className="mt-4 space-y-2">
                {p.tenancies.length === 0 ? <p className="text-sm text-amber-700">Currently vacant</p> : p.tenancies.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm border-t border-slate-100 pt-2">
                    <span>{t.renter_name} · {fmtDate(t.start_date)} – {fmtDate(t.end_date)}</span>
                    <span className="font-mono">{money(t.rent_amount, cur)} / {t.rent_frequency}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Issue({ m, reload }) {
  const [text, setText] = useState("");
  const send = async () => {
    try { await api.post(`/landlord/issues/${m.id}/notes`, { text }); setText(""); toast.success("Reply sent to your agent"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="panel" data-testid={`ll-issue-${m.id}`}>
      <div className="flex justify-between gap-3">
        <div><div className="font-semibold">{m.title}</div><div className="text-xs text-slate-500">{m.property_name} · reported {fmtDate(m.created_at)}{m.reported_by === "renter" && " by the tenant"}</div></div>
        <div className="flex gap-2"><StatusBadge status={m.priority} /><StatusBadge status={m.status} /></div>
      </div>
      {m.description && <p className="text-sm mt-3">{m.description}</p>}
      {(m.contractor || m.cost != null) && <p className="text-xs text-slate-500 mt-2">{m.contractor && `Contractor: ${m.contractor}`} {m.cost != null && `· Cost: £${m.cost}`}</p>}
      <div className="mt-4 space-y-2">{m.notes.map((n, i) => (
        <div key={i} className={`rounded-lg p-3 text-sm ${n.role === "landlord" ? "bg-emerald-50 border border-emerald-100" : "bg-slate-50 border border-slate-200"}`}>{n.text}<div className="text-xs text-slate-500 mt-1">{n.by} · {fmtDate(n.at)}</div></div>
      ))}</div>
      <div className="flex gap-2 mt-3">
        <Textarea rows={2} placeholder="Reply to your agent (e.g. approve the quote)" value={text} onChange={(e) => setText(e.target.value)} data-testid={`ll-issue-reply-${m.id}`} />
        <Button onClick={send} disabled={!text.trim()} className="bg-emerald-700 hover:bg-emerald-800 text-white self-end" data-testid={`ll-issue-send-${m.id}`}>Send</Button>
      </div>
    </div>
  );
}

function Issues() {
  const { data, reload } = useFetch("/landlord/issues");
  return (
    <div data-testid="landlord-issues-page">
      <PageHeader eyebrow="Repairs" title="Issues" subtitle="Maintenance your agent has shared with you. Reply to approve work or ask questions." />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={Wrench} title="Nothing shared with you" /> : <div className="space-y-4">{data.map((m) => <Issue key={m.id} m={m} reload={reload} />)}</div>}
    </div>
  );
}

function Statements() {
  const months = Array.from({ length: 12 }, (_, i) => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i); return d.toISOString().slice(0, 7); });
  return (
    <div data-testid="landlord-statements-page">
      <PageHeader eyebrow="Accounts" title="Statements" subtitle="Monthly statements: rent received, costs, management fee and net payable." />
      <div className="panel divide-y divide-slate-100 p-0">
        {months.map((m) => (
          <div key={m} className="flex items-center justify-between px-6 py-3">
            <span className="font-medium">{new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</span>
            <Button size="sm" variant="outline" onClick={() => downloadFile(`/landlord/statement?month=${m}`, `statement-${m}.pdf`).catch((e) => toast.error(errMsg(e)))} data-testid={`ll-statement-${m}`}><FileDown className="w-4 h-4 mr-1" />Download PDF</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const INTEREST = { high: ["active", "Very interested"], medium: ["due", "Some interest"], low: ["skipped", "Little interest"], none: ["failed", "Not interested"] };
const when = (s) => new Date(`${s}:00`).toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

function Viewings() {
  const { data } = useFetch("/landlord/viewings");
  const upcoming = (data || []).filter((v) => v.status === "confirmed").reverse();
  const done = (data || []).filter((v) => v.status === "completed");
  return (
    <div data-testid="landlord-viewings-page">
      <PageHeader eyebrow="Your properties" title="Viewings" subtitle="Booked viewings of your properties and the feedback your agent has shared." />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={CalendarDays} title="No viewings yet" text="Confirmed viewings and shared feedback will appear here." /> : <>
        {upcoming.length > 0 && <div className="panel mb-6" data-testid="ll-upcoming-viewings"><h3 className="text-lg font-bold mb-3">Booked viewings</h3>
          <div className="divide-y divide-slate-100">{upcoming.map((v) => <div key={v.id} className="py-2 flex justify-between text-sm" data-testid={`ll-viewing-${v.id}`}><span>{v.listing_title}{v.visitor && <span className="text-slate-500"> · {v.visitor}</span>}</span><span className="font-medium">{when(v.start)}</span></div>)}</div></div>}
        <h3 className="text-lg font-bold mb-3">Feedback</h3>
        {done.length === 0 ? <EmptyState icon={CalendarDays} title="No feedback shared yet" /> : <div className="space-y-3">{done.map((v) => (
          <div key={v.id} className="panel" data-testid={`ll-feedback-${v.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><div><div className="font-semibold">{v.listing_title}</div><div className="text-xs text-slate-500">{when(v.start)} · {v.visitor}</div></div>
              <StatusBadge status={INTEREST[v.feedback.interest][0]} label={INTEREST[v.feedback.interest][1]} /></div>
            {v.feedback.comments && <p className="text-sm text-slate-700 mt-2 whitespace-pre-wrap">{v.feedback.comments}</p>}
          </div>))}</div>}
      </>}
    </div>
  );
}

function Offers() {
  const { data, reload } = useFetch("/landlord/offers");
  const { data: ov } = useFetch("/landlord/overview");
  const cur = ov?.agent.currency;
  return (
    <div data-testid="landlord-offers-page">
      <PageHeader eyebrow="Your properties" title="Offers" subtitle={`Offers your agent has shared with you. Reply in writing below — ${ov?.agent.name || "your agent"} records the final decision.`} />
      {!data ? <Loading /> : data.length === 0 ? <EmptyState icon={HandCoins} title="No offers shared yet" /> : <div className="space-y-4">{data.map((o) => (
        <div key={o.id} className="panel" data-testid={`ll-offer-${o.id}`}>
          <div className="flex flex-wrap items-center gap-2"><span className="font-display text-2xl font-extrabold">{fmtMoney(o.amount, cur, o.channel)}</span>
            <StatusBadge status={OFFER_STATUS[o.status][0]} label={OFFER_STATUS[o.status][1]} testid={`ll-offer-status-${o.id}`} /></div>
          <div className="text-sm mt-1"><b>{o.listing_title}</b> · from {o.applicant} · {fmtDate(o.created_at)}</div>
          {o.asking_price > 0 && <div className="text-xs text-slate-500">Asking {fmtMoney(o.asking_price, cur, o.channel)} · {Math.round((o.amount / o.asking_price) * 100)}% of asking</div>}
          {o.conditions && <p className="text-sm text-slate-600 mt-2 whitespace-pre-wrap">{o.conditions}</p>}
          {o.status === "countered" && <p className="text-sm mt-2">Counter-offer: <b>{fmtMoney(o.counter_amount, cur, o.channel)}</b></p>}
          {o.decision_note && <p className="text-sm text-slate-600 mt-1 italic">“{o.decision_note}”</p>}
          <OfferNotes offer={o} url={`/landlord/offers/${o.id}/notes`} me="landlord" onSent={reload} />
        </div>))}</div>}
    </div>
  );
}

export default function LandlordApp() {
  const { session } = useAuth();
  const items = [
    { id: "ll-home", to: "/landlord", end: true, label: "My portfolio", icon: Home },
    { id: "ll-issues", to: "/landlord/issues", label: "Issues", icon: Wrench },
    { id: "ll-viewings", to: "/landlord/viewings", label: "Viewings", icon: CalendarDays },
    { id: "ll-offers", to: "/landlord/offers", label: "Offers", icon: HandCoins },
    { id: "ll-statements", to: "/landlord/statements", label: "Statements", icon: FileText },
    { id: "ll-account", to: "/landlord/account", label: "Account", icon: UserCircle },
  ];
  return (
    <Shell items={items} title={session.user.name} subtitle="Landlord">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="issues" element={<Issues />} />
        <Route path="viewings" element={<Viewings />} />
        <Route path="offers" element={<Offers />} />
        <Route path="statements" element={<Statements />} />
        <Route path="account" element={<div><PageHeader eyebrow="Account" title="Your account" /><AccountCard /></div>} />
        <Route path="*" element={<Navigate to="/landlord" replace />} />
      </Routes>
    </Shell>
  );
}
